import React, { useState, useEffect } from "react";
import styles from "./report.module.css";
import {
    Stack,
    Pagination,
    Snackbar,
    Checkbox,
    Tabs,
    Tab,
} from "@mui/material/";
import Alert from "@material-ui/lab/Alert";
import { Typography, Button } from "@material-ui/core";
import deleteIcon from "./report_icons/delete.png";
import printIcon from "./report_icons/print.png";
import reviewIcon from "./report_icons/review.png";
import searchIcon from "./report_icons/search.png";
import downLoadIcon from "./report_icons/download.png";
import DeleteModal from "./DeleteModal";
import ReviewModal from "./ReviewModal";
import ViewReport from "./Sleep-Report/ViewReport";
import WalkTestModal from "./WalkTestModal";
import SleepReportModal from "./Sleep-Report/SleepReportModal";
import { useSelector, useDispatch } from "react-redux";
import { setWalkTestModal } from "../../store/actions/walkTestAction";
import axios from "../../utils/lib/axios";
import useAxiosPrivate from "utils/hooks/useAxiosPrivate";
import ReportGeneratedSuccessAlert from "./Sleep-Report/ReportGeneratedSuccessAlert";

export default function SleepReport() {
    const axiosPrivate = useAxiosPrivate();
    const dispatch = useDispatch();
    const walkTestModal = useSelector(
        (state) => state.walkTest?.isModalOpen || false,
    );
    const role = useSelector((state) => state.auth.role);
    const [pageNumber, setPageNumber] = useState(1);
    const [count, setCount] = useState(0);
    const [selected, setSelected] = useState([]);
    const [alert, setAlert] = useState({
        showAlert: false,
        severity: "success",
        message: "",
    });
    const [tabValue, setTabValue] = useState(0); // 0 for Completed, 1 for Waiting for Review
    // const [reportNav, setReportNav] = useState("6mwt"); // "6mwt" | "sleep" - which report section is active
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [reviewModalOpen, setReviewModalOpen] = useState(false);
    const [viewReportModalOpen, setViewReportModalOpen] = useState(false);
    const [walkTestModalOpen, setWalkTestModalOpen] = useState(false);
    const [sleepReportModalOpen, setSleepReportModalOpen] = useState(false);
    const [selectedReport, setSelectedReport] = useState(null);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [completedReports, setCompletedReports] = useState([]);
    const [waitingReports, setWaitingReports] = useState([]);
    const [waitingCount, setWaitingCount] = useState(0);
    const [waitingPageNumber, setWaitingPageNumber] = useState(1);
    const [devices, setDevices] = useState([]);
    const [admins, setAdmins] = useState([]);
    const [icdTens, setIcdTens] = useState([]);
    const [reportGeneratedSuccessAlert, setReportGeneratedSuccessAlert] =
        useState({
            showAlert: false,
            alertDetails: {},
        });
    useEffect(() => {
        if (tabValue === 0) {
            loadCompletedData();
        } else {
            loadWaitingData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabValue, pageNumber, waitingPageNumber]);

    useEffect(() => {
        loadPlayers();
        loadAdminMembers();
        loadICD();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadCompletedData = async () => {
        try {
            const { data } = await axios.get(
                `/sleep/reports/com/${pageNumber}`,
            );

            //setCount(data.total_pages);
            setCompletedReports(data.data);
            setCount(data.total_pages ?? 1);
        } catch (error) {
            setAlert({
                showAlert: true,
                severity: "error",
                message: "Error loading completed reports",
            });
        }
    };

    const loadWaitingData = async () => {
        try {
            const { data } = await axios.get(
                `/sleep/reports/rev/${waitingPageNumber}`,
            );
            setWaitingCount(data.total_pages ?? 1);
            setWaitingReports(data.data);
        } catch (error) {
            setAlert({
                showAlert: true,
                severity: "error",
                message: "Error loading waiting reports",
            });
        }
    };

    const loadPlayers = async () => {
        try {
            const { data } = await axios.get(`/device/list`);
            setDevices(
                data.data
                    // ?.filter((x) => x.status === "ACTIVE")
                    ?.map((x) => ({
                        value: x.id,
                        label: x.name,
                    })),
            );
        } catch (error) {
            setAlert({
                showAlert: true,
                severity: "error",
                message: "Error loading waiting reports",
            });
        }
    };

    const loadAdminMembers = async () => {
        try {
            const { data } = await axios.get(`/team/admins`);
            setAdmins(
                data.data
                    .filter((x) => x.role === "USER_PHYSICIAN")
                    ?.map((item) => ({
                        value: item.id,
                        label: `${item.first_name} ${item.last_name}`,
                    })),
            );
        } catch (error) {
            setAlert({
                showAlert: true,
                severity: "error",
                message: "Error loading waiting reports",
            });
        }
    };

    const loadICD = async () => {
        try {
            const { data } = await axios.get(`/6mwt/icd-10`);
            setIcdTens(
                data.data?.map((item) => ({
                    value: item?.code,
                    label: item?.description,
                })),
            );
        } catch (error) {
            setAlert({
                showAlert: true,
                severity: "error",
                message: "Error loading waiting reports",
            });
        }
    };

    const handleSelect = (id) => {
        const selectedIndex = selected.indexOf(id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }

        setSelected(newSelected);
    };

    const isSelected = (id) => selected.indexOf(id) !== -1;

    const handleChange = (event, value) => {
        setPageNumber(value);
    };

    const handleClose = () => {
        setAlert({
            showAlert: false,
            severity: "success",
            message: "",
        });
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        // Reset pagination when switching tabs
        if (newValue === 0) {
            setPageNumber(1);
        } else {
            setWaitingPageNumber(1);
        }
    };

    const handleDeleteClick = (item) => {
        setItemToDelete(item);
        setDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            // Implement delete logic here
            const { data } = await axios.delete(
                `/sleep/reports/${itemToDelete?.id}`,
                {
                    data: {
                        report_id: itemToDelete?.id,
                    },
                },
            );
            if (data?.status === "OK") {
                setDeleteModalOpen(false);
                setAlert({
                    showAlert: true,
                    severity: "success",
                    message: "Report deleted successfully",
                });
                if (tabValue === 0) {
                    loadCompletedData();
                } else {
                    loadWaitingData();
                }
            } else {
                setAlert({
                    showAlert: true,
                    severity: "error",
                    message: data?.message,
                });
            }
        } catch (error) {
            setAlert({
                showAlert: true,
                severity: "error",
                message: "Failed to delete report",
            });
        }
    };

    const handlePrint = async (item) => {
        try {
            const { data } = await axiosPrivate.get(item.report_pdf, {
                responseType: "blob",
            });
            const objectUrl = URL.createObjectURL(
                new Blob([data], { type: "application/pdf" }),
            );
            window.open(objectUrl, "_blank");
        } catch {
            setAlert({
                showAlert: true,
                severity: "error",
                message: "Failed to open the report. Please try again.",
            });
        }
    };

    const handleDownload = async (item) => {
        try {
            const { data } = await axiosPrivate.get(item.report_pdf, {
                responseType: "blob",
            });
            const objectUrl = URL.createObjectURL(
                new Blob([data], { type: "application/pdf" }),
            );
            const link = document.createElement("a");
            link.href = objectUrl;
            link.download = item.file_name || "report.pdf";
            link.click();
            URL.revokeObjectURL(objectUrl);
        } catch {
            setAlert({
                showAlert: true,
                severity: "error",
                message: "Failed to download the report. Please try again.",
            });
        }
    };

    const handleReviewClick = (item) => {
        setSelectedReport(item);
        setReviewModalOpen(true);
    };

    const handleViewClick = (item) => {
        setSelectedReport(item);
        setViewReportModalOpen(true);
    };

    const handleReviewSubmit = async (interpretation) => {
        try {
            // Implement review submission logic here
            // Implement delete logic here
            const { data } = await axios.patch(`/sleep/approve-report`, {
                report_id: selectedReport?.id,
                physician_interpretation: interpretation,
            });
            if (data?.status === "OK") {
                setReviewModalOpen(false);
                // setAlert({
                //     showAlert: true,
                //     severity: "success",
                //     message: "Report submitted successfully",
                // });
                // After Sign & Save (physician review), always show Completed tab
                setTabValue(0);
                setPageNumber(1);
                loadWaitingData();
                loadCompletedData();

                const completedReportName =
                    (data?.file_name
                        ? String(data.file_name).replace(/\.pdf$/i, "")
                        : undefined) ??
                    data?.report_name ??
                    data?.report_name ??
                    selectedReport?.report_name ??
                    selectedReport?.name ??
                    selectedReport?.title ??
                    (selectedReport?.file_name
                        ? String(selectedReport.file_name).replace(/\.pdf$/i, "")
                        : undefined);
                setReportGeneratedSuccessAlert({
                    showAlert: true,
                    alertDetails: {
                        report_id: data?.report_id ?? selectedReport?.report_id,
                        id: data?.report_id ?? selectedReport?.id,
                        report_status: data?.report_status,
                        file_name: data?.file_name ?? selectedReport?.file_name,
                        report_pdf: data?.pdf_url ?? selectedReport?.report_pdf,
                        pdf_url: data?.pdf_url,
                        status: "completed",
                        report_name: completedReportName,
                    },
                });
            } else {
                setAlert({
                    showAlert: true,
                    severity: "error",
                    message: data?.message,
                });
            }
        } catch (error) {
            setAlert({
                showAlert: true,
                severity: "error",
                message: "Failed to submit review",
            });
        }
    };

    // Add this render helper function
    const renderActionButtons = (item, status) => {
        return (
            <div className={styles.actionButtons}>
                {status === "completed" && (
                    <Button
                        className={styles.actionButton}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleReviewClick(item);
                        }}
                    >
                        <img
                            src={searchIcon}
                            alt="Walk Test"
                            className={styles.actionIcon}
                        />
                    </Button>
                )}
                {status === "waiting" && role === "USER_PHYSICIAN" && (
                    <Button
                        className={styles.actionButton}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleReviewClick(item);
                        }}
                    >
                        <img
                            src={reviewIcon}
                            alt="Review"
                            className={styles.actionIcon}
                        />
                    </Button>
                )}

                <Button
                    className={styles.actionButton}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(item);
                    }}
                >
                    <img
                        src={downLoadIcon}
                        alt="Download"
                        className={styles.actionIcon}
                    />
                </Button>
                <Button
                    className={styles.actionButton}
                    onClick={(e) => {
                        e.stopPropagation();
                        handlePrint(item);
                    }}
                >
                    <img
                        src={printIcon}
                        alt="Print"
                        className={styles.actionIcon}
                    />
                </Button>
                <Button
                    className={styles.actionButton}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(item);
                    }}
                >
                    <img
                        src={deleteIcon}
                        alt="Delete"
                        className={styles.actionIcon}
                    />
                </Button>
            </div>
        );
    };

    useEffect(() => {
        // Close modal when component unmounts
        return () => {
            dispatch(setWalkTestModal(false));
        };
    }, [dispatch]);

    // Update useEffect to watch for walkTestModal changes
    useEffect(() => {
        if (walkTestModal) {
            setWalkTestModalOpen(true);
        }
    }, [walkTestModal]);

    // Update modal close handler to handle both local and redux state
    const handleWalkTestModalClose = () => {
        setWalkTestModalOpen(false);
        dispatch(setWalkTestModal(false));
    };

    const handleSleepReportModalClose = () => {
        setSleepReportModalOpen(false);
    };

    return (
        <>
            <Snackbar
                open={alert.showAlert}
                autoHideDuration={4000}
                onClose={handleClose}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Alert severity={alert.severity} onClose={handleClose}>
                    {alert.message}
                </Alert>
            </Snackbar>
            {reportGeneratedSuccessAlert.showAlert && (
                <ReportGeneratedSuccessAlert
                    alertDetails={reportGeneratedSuccessAlert.alertDetails}
                    setReportGeneratedSuccessAlert={
                        setReportGeneratedSuccessAlert
                    }
                    handleViewClick={handleViewClick}
                />
            )}

            <div className={styles.tableContent}>
                <div className={styles.topSection}>
                    <div className={styles.tabNavigation}>
                        <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            className={styles.tabs}
                            TabIndicatorProps={{
                                style: {
                                    backgroundColor: "#0F9BB0",
                                },
                            }}
                        >
                            <Tab
                                label="Completed"
                                className={styles.tab}
                                disableRipple
                            />
                            <Tab
                                label="Waiting for Review"
                                className={styles.tab}
                                disableRipple
                            />
                        </Tabs>
                    </div>

                    <Button
                        variant="contained"
                        onClick={() => setSleepReportModalOpen(true)}
                        style={{
                            backgroundColor: "#0F9BB0",
                            color: "white",
                            textTransform: "none",
                            fontFamily: "Poppins",
                            fontSize: "11px",
                        }}
                    >
                        Create Sleep Report
                    </Button>
                </div>

                {tabValue === 0 ? (
                    // Completed Reports Table
                    <>
                        <div className={styles.tableHead}>
                            <div className={styles.header}>
                                <div className={styles.checkboxCell}>
                                    {/* <Checkbox
                        color="primary"
                        indeterminate={
                          selected.length > 0 && selected.length < reports.length
                        }
                        checked={
                          reports.length > 0 && selected.length === reports.length
                        }
                        onChange={handleSelectAll}
                      /> */}
                                </div>
                                <div className={styles.tittle1}>
                                    <Typography
                                        variant="h5"
                                        style={{
                                            fontSize: "14px",
                                            lineHeight: "1rem",
                                        }}
                                    >
                                        Date
                                    </Typography>
                                </div>
                                <div className={styles.tittle2}>
                                    <Typography
                                        variant="h5"
                                        style={{
                                            fontSize: "14px",
                                            lineHeight: "1rem",
                                        }}
                                    >
                                        Time
                                    </Typography>
                                </div>
                                <div className={styles.tittle3}>
                                    <Typography
                                        variant="h5"
                                        style={{
                                            fontSize: "14px",
                                            lineHeight: "1rem",
                                        }}
                                    >
                                        File Name
                                    </Typography>
                                </div>
                                <div className={styles.tittle3}>
                                    <Typography
                                        variant="h5"
                                        style={{
                                            fontSize: "14px",
                                            lineHeight: "1rem",
                                        }}
                                    >
                                        Physician
                                    </Typography>
                                </div>
                                {/* <div className={styles.tittle3}>
                      <Typography
                        variant="h5"
                        style={{ fontSize: "0.78rem", lineHeight: "1rem" }}
                      >
                        Reviewing Physician
                      </Typography>
                    </div> */}
                                <div className={styles.actionsCell}>
                                    {/* Empty header for actions column */}
                                    <Typography
                                        variant="h5"
                                        style={{
                                            fontSize: "14px",
                                            lineHeight: "1rem",
                                        }}
                                    >
                                        Action
                                    </Typography>
                                </div>
                            </div>
                        </div>
                        {completedReports.map((item, index) => {
                            const isItemSelected = isSelected(item.id);

                            return (
                                <div
                                    className={styles.container}
                                    style={{ cursor: "pointer" }}
                                    key={index}
                                >
                                    <div
                                        className={`${styles.tBody} ${
                                            isItemSelected
                                                ? styles.selectedRow
                                                : ""
                                        }`}
                                    >
                                        <div className={styles.checkboxCell}>
                                            <Checkbox
                                                color="primary"
                                                checked={isItemSelected}
                                                onChange={() =>
                                                    handleSelect(item.id)
                                                }
                                                sx={{
                                                    color: "#0F9BB0",
                                                    "&.Mui-checked": {
                                                        color: "#0F9BB0",
                                                    },
                                                    "& .MuiSvgIcon-root": {
                                                        fontSize: 24,
                                                        strokeWidth: 2,
                                                    },
                                                }}
                                            />
                                        </div>
                                        <div className={styles.tittle1}>
                                            <Typography
                                                variant="h4"
                                                style={{
                                                    fontSize: "14px",
                                                    lineHeight: "1rem",
                                                }}
                                            >
                                                {item.date}
                                            </Typography>
                                        </div>
                                        <div className={styles.tittle2}>
                                            <Typography
                                                variant="h4"
                                                style={{
                                                    fontSize: "14px",
                                                    lineHeight: "1rem",
                                                }}
                                            >
                                                {item.time}
                                            </Typography>
                                        </div>
                                        <div
                                            className={styles.tittle3}
                                            title={item.file_name}
                                        >
                                            <Typography
                                                variant="h4"
                                                noWrap
                                                style={{
                                                    fontSize: "14px",
                                                    lineHeight: "1rem",
                                                }}
                                            >
                                                {item.file_name}
                                            </Typography>
                                        </div>
                                        <div className={styles.tittle3}>
                                            <Typography
                                                variant="h4"
                                                style={{
                                                    fontSize: "14px",
                                                    lineHeight: "1rem",
                                                }}
                                            >
                                                {item?.physician}
                                            </Typography>
                                        </div>
                                        {/* <div className={styles.tittle3}>
                          <Typography
                            variant="body1"
                            style={{
                              fontSize: "0.69rem",
                              lineHeight: "1rem",
                            }}
                          >
                            {item.reviewing_physician_name}
                          </Typography>
                        </div> */}
                                        <div className={styles.actionsCell}>
                                            {renderActionButtons(
                                                item,
                                                "completed",
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        <div className={styles.Pagination}>
                            <Stack spacing={2}>
                                <Pagination
                                    count={count}
                                    page={pageNumber}
                                    variant="outlined"
                                    shape="rounded"
                                    onChange={handleChange}
                                    style={{
                                        transform: "scale(0.8)",
                                    }}
                                />
                            </Stack>
                        </div>
                    </>
                ) : (
                    <>
                        <div className={styles.tableHead}>
                            <div className={styles.header}>
                                <div className={styles.checkboxCell}></div>
                                <div className={styles.tittle1}>
                                    <Typography
                                        variant="h5"
                                        style={{
                                            fontSize: "14px",
                                            lineHeight: "1rem",
                                        }}
                                    >
                                        Date
                                    </Typography>
                                </div>
                                <div className={styles.tittle2}>
                                    <Typography
                                        variant="h5"
                                        style={{
                                            fontSize: "14px",
                                            lineHeight: "1rem",
                                        }}
                                    >
                                        Time
                                    </Typography>
                                </div>
                                <div className={styles.tittle3}>
                                    <Typography
                                        variant="h5"
                                        style={{
                                            fontSize: "14px",
                                            lineHeight: "1rem",
                                        }}
                                    >
                                        File Name
                                    </Typography>
                                </div>
                                <div className={styles.tittle3}>
                                    <Typography
                                        variant="h5"
                                        style={{
                                            fontSize: "14px",
                                            lineHeight: "1rem",
                                        }}
                                    >
                                        Technician
                                    </Typography>
                                </div>
                                <div className={styles.actionsCell}>
                                    {/* Empty header for actions column */}
                                    <Typography
                                        variant="h5"
                                        style={{
                                            fontSize: "14px",
                                            lineHeight: "1rem",
                                        }}
                                    >
                                        Action
                                    </Typography>
                                </div>
                            </div>
                        </div>
                        {waitingReports.map((item, index) => {
                            const isItemSelected = isSelected(item.id);

                            return (
                                <div
                                    className={styles.container}
                                    style={{ cursor: "pointer" }}
                                    key={index}
                                >
                                    <div
                                        className={`${styles.tBody} ${
                                            isItemSelected
                                                ? styles.selectedRow
                                                : ""
                                        }`}
                                    >
                                        <div className={styles.checkboxCell}>
                                            <Checkbox
                                                color="primary"
                                                checked={isItemSelected}
                                                onChange={() =>
                                                    handleSelect(item.id)
                                                }
                                                sx={{
                                                    color: "#0F9BB0",
                                                    "&.Mui-checked": {
                                                        color: "#0F9BB0",
                                                    },
                                                    "& .MuiSvgIcon-root": {
                                                        fontSize: 24,
                                                        strokeWidth: 2,
                                                    },
                                                }}
                                            />
                                        </div>
                                        <div className={styles.tittle1}>
                                            <Typography
                                                variant="h4"
                                                style={{
                                                    fontSize: "14px",
                                                    lineHeight: "1rem",
                                                }}
                                            >
                                                {item.date}
                                            </Typography>
                                        </div>
                                        <div className={styles.tittle2}>
                                            <Typography
                                                variant="h4"
                                                style={{
                                                    fontSize: "14px",
                                                    lineHeight: "1rem",
                                                }}
                                            >
                                                {item.time}
                                            </Typography>
                                        </div>
                                        <div
                                            className={styles.tittle3}
                                            title={item.file_name}
                                        >
                                            <Typography
                                                variant="h4"
                                                noWrap
                                                style={{
                                                    fontSize: "14px",
                                                    lineHeight: "1rem",
                                                }}
                                            >
                                                {item.file_name}
                                            </Typography>
                                        </div>
                                        <div className={styles.tittle3}>
                                            <Typography
                                                variant="h4"
                                                style={{
                                                    fontSize: "14px",
                                                    lineHeight: "1rem",
                                                }}
                                            >
                                                {item?.technician}
                                            </Typography>
                                        </div>
                                        <div className={styles.actionsCell}>
                                            {renderActionButtons(
                                                item,
                                                "waiting",
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        <div className={styles.Pagination}>
                            <Stack spacing={2}>
                                <Pagination
                                    count={waitingCount}
                                    page={waitingPageNumber}
                                    variant="outlined"
                                    shape="rounded"
                                    onChange={(e, value) =>
                                        setWaitingPageNumber(value)
                                    }
                                    style={{
                                        transform: "scale(0.8)",
                                    }}
                                />
                            </Stack>
                        </div>
                    </>
                )}
            </div>

            <DeleteModal
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={handleDeleteConfirm}
            />
            <ReviewModal
                open={reviewModalOpen}
                onClose={() => {
                    setReviewModalOpen(false);
                    setSelectedReport(null);
                }}
                onSubmit={handleReviewSubmit}
                selectedReport={selectedReport}
                tabValue={tabValue}
            />
            <ViewReport
                open={viewReportModalOpen}
                onClose={() => setViewReportModalOpen(false)}
                selectedReport={selectedReport}
            />
            <WalkTestModal
                open={walkTestModalOpen}
                onClose={handleWalkTestModalClose}
                admins={admins}
                loadCompletedData={loadCompletedData}
                loadWaitingData={loadWaitingData}
                setAlert={setAlert}
                icdTens={icdTens}
                devices={devices}
            />
            <SleepReportModal
                open={sleepReportModalOpen}
                setSleepReportModalOpen={setSleepReportModalOpen}
                onClose={handleSleepReportModalClose}
                admins={admins}
                loadCompletedData={loadCompletedData}
                loadWaitingData={loadWaitingData}
                setAlert={setAlert}
                icdTens={icdTens}
                devices={devices}
                setReportGeneratedSuccessAlert={setReportGeneratedSuccessAlert}
                onSleepReportGenerated={() => {
                    setTabValue(1);
                    setWaitingPageNumber(1);
                }}
            />
        </>
    );
}
